import React, { useState } from 'react';
import { usePageMeta } from '../lib/usePageMeta';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import TierCard from '../components/TierCard';
import CTABar from '../components/CTABar';
import InterestFormModal from '../components/InterestFormModal';

const Services: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  usePageMeta(
    'Services + Pricing | Parsant',
    'Three engagement modes. You know the number before the work starts. Plan ($15K-$30K fixed-fee), Embed ($15K-$25K monthly), Hand-off ($15K-$30K fixed-fee).'
  );

  return (
    <main className="bg-void-black min-h-screen">
      <Nav onContactClick={() => setIsFormOpen(true)} />

      <section className="pt-32 pb-16">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow eyebrow-accent mb-4">Services</div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-signal-white mb-6 tracking-tight leading-[1.08]">
            Three engagement modes.<br/>You know the number first.
          </h1>
          <p className="text-xl text-steel measure">
            Fixed scope and a fixed number before any work starts. Each mode covers a different
            stage, whether that is the diagnosis, the build, or the exit. Pick the one that
            matches where you are.
          </p>
        </div>
      </section>

      <section className="section border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-grid-line">
            <TierCard
              tierNumber="01"
              name="Plan"
              price="$15K-$30K"
              duration="2-4 weeks"
              format="Fixed-fee"
              deliverables={[
                'A map of how revenue actually moves through the company',
                'A definitions layer covering what a prospect, an opportunity, and a won deal each mean',
                'A sequenced roadmap of what to build, buy, or retire',
                'Board-ready deliverable in week 4, compliance review included',
              ]}
              bestFitIf="You need a candid, time-boxed read on what to build, buy, or retire before committing to a longer engagement."
              anchor="plan"
            />
            <TierCard
              tierNumber="02"
              name="Embed"
              price="$15K-$25K/mo"
              duration="3-6 months"
              format="Monthly subscription"
              deliverables={[
                'A senior lead instruments the pipeline and forecast and runs the weekly rhythm',
                'The definitions layer adopted across marketing, sales, and finance by month two',
                'Forecast accuracy inside an agreed tolerance by month four',
                'Every stage carrying a dwell time and a recorded loss reason',
              ]}
              bestFitIf="The roadmap is clear and you need senior operating capacity to execute alongside your team."
              anchor="embed"
            />
            <TierCard
              tierNumber="03"
              name="Hand-off"
              price="$15K-$30K"
              duration="1-2 months"
              format="Fixed-fee"
              deliverables={[
                'Runbooks and systems diagrams for the system as built',
                'Error handling, rollback paths, and the model-swap policy',
                'A named internal owner running it in shadow mode before close',
                'Exit criteria signed off before close',
              ]}
              bestFitIf="The system runs. You need it documented, transitioned to an internal owner, and Parsant out cleanly."
              anchor="handoff"
            />
          </div>
        </div>
      </section>

      <section className="section-loose border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">12-month envelope</div>
          <h2 className="text-3xl font-bold text-signal-white mb-6">$75K-$210K</h2>
          <p className="text-lg text-steel measure">
            Most engagements start with Plan and extend through Embed, and every sequence ends in
            Hand-off. The envelope reflects one Plan, three to six months of Embed, and one
            Hand-off.
          </p>
        </div>
      </section>

      <section className="section-tight border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">Out of scope</div>
          <ul className="text-steel space-y-2 text-lg measure">
            <li>Parsant does not take an interim executive role.</li>
            <li>Parsant does not manage the sales team.</li>
            <li>Parsant does not replace internal technical leadership.</li>
            <li>Parsant does not require equity or a board seat.</li>
            <li>Parsant does not pitch additional service lines mid-engagement.</li>
          </ul>
        </div>
      </section>

      <section className="section-loose border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">Capacity</div>
          <h2 className="text-3xl font-bold text-signal-white mb-6 max-w-4xl">A senior operator on every call and every build.</h2>
          <p className="text-lg text-steel measure mb-12">
            The roster stays short by design, so whoever scoped the engagement is still running it
            at Hand-off.
          </p>
          <CTABar onContactClick={() => setIsFormOpen(true)} />
        </div>
      </section>

      <Footer />
      <InterestFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </main>
  );
};

export default Services;
